import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// 'default' draws the ordinary header of the layout (tabs or classic labels)
// and only lets the page replace the back action with `onBack`.
export type CashierHeaderVariant =
  | 'tabs'
  | 'title-row'
  | 'top-bar'
  | 'minimal-back-close'
  | 'close-only'
  | 'classic'
  | 'default'
  | 'none';

export interface CashierHeaderConfig {
  variant: CashierHeaderVariant;

  title?: string;
  titleLayout?: 'inline' | 'stacked';
  showStickyTitle?: boolean;
  onBack?: () => void;
  showBack?: boolean;
  backDisabled?: boolean;
  showClose?: boolean;
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  titleClassName?: string;

  transactionType?: string;
  showHeaderElements?: boolean;
  activeTab?: string;
  onTabClick?: (tab: string) => void;
  withPending?: boolean;
  balance?: string;
  balanceError?: boolean;
  // True while the page itself knows a submission is under way, before the
  // shared state does. The tab bar then shows the own tab only, not clickable.
  submitLocked?: boolean;
  // Marks a title-row configuration that asks for the tab bar. The header draws
  // the tab bar for every non-stacked title-row itself (isSecondaryStep), and
  // the payment type setting decides whether its tabs show; the flag only
  // documents the request.
  showTabs?: boolean;

  className?: string;
}

interface HeaderContextValue {
  config: CashierHeaderConfig | null;
  setConfig: (config: CashierHeaderConfig | null) => void;
  clearConfig: () => void;
}

const HeaderContext = createContext<HeaderContextValue | null>(null);

export const HeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfigState] = useState<CashierHeaderConfig | null>(null);

  const setConfig = useCallback((next: CashierHeaderConfig | null) => {
    setConfigState(next);
  }, []);

  const clearConfig = useCallback(() => {
    setConfigState(null);
  }, []);

  const value = useMemo(
    () => ({ config, setConfig, clearConfig }),
    [config, setConfig, clearConfig],
  );

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
};

const useHeaderContext = (): HeaderContextValue | null => useContext(HeaderContext);

export const useHeaderState = (): CashierHeaderConfig | null => {
  const ctx = useHeaderContext();
  return ctx?.config ?? null;
};

export const useHeaderConfig = (
  config: CashierHeaderConfig | null | undefined,
  deps: React.DependencyList,
): void => {
  const ctx = useHeaderContext();

  useIsomorphicLayoutEffect(() => {
    if (!ctx || config === undefined) return undefined;
    ctx.setConfig(config);
    return () => ctx.clearConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
